import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Tabs, Tab, Avatar, IconButton,
  Button, CircularProgress, Alert, Snackbar, Skeleton, Badge, Tooltip,
  Stack, Fade, Divider, Link
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SaveIcon from '@mui/icons-material/Save';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuth } from '../../contexts/AuthContext';
import OrgManagement from './components/OrgManagement';

import profileApi from './profileApi';
import BasicDetails from './components/BasicDetails';
import ContactInfo from './components/ContactInfo';
import AddressManager from './components/AddressManager';
import AccountSettings from './components/AccountSettings';
import CorporateSection from './components/CorporateSection';
import NotificationsSettings from './components/NotificationsSettings';
import ImageCropper from '../../components/profile/ImageCropper';
import BillingCredits from './components/BillingCredits';
import PrivacySettings from './components/PrivacySettings';
import IntegrationsSettings from './components/IntegrationsSettings';

const PERSONAL_TABS = ['Personal', 'Contact', 'Account', 'Privacy', 'Business', 'Integrations', 'Notifications', 'Billing'];

// Tab visibility per org role. Indices match PERSONAL_TABS.
// admin=0, organiser=1, viewer=2 → true=visible
const ORG_TAB_VISIBILITY = {
  //            Personal  Contact  Account  Privacy  Business  Integrations  Notifications  Billing
  admin:     [  true,     true,    true,    true,    true,     true,         true,          true   ],
  organiser: [  true,     true,    false,   false,   true,     true,         true,          false  ],
  viewer:    [  true,     false,   false,   false,   false,    false,        true,          false  ],
};

const MAX_PHOTO_BYTES = 5 * 1024 * 1024; // 5 MB

const Profile = () => {
  const { user, isOrgContext, orgRole, activeOrg } = useAuth();

  // Build visible tab list based on context
  const visibilityRow = isOrgContext ? (ORG_TAB_VISIBILITY[orgRole] || ORG_TAB_VISIBILITY.viewer) : null;
  const TABS = visibilityRow
    ? PERSONAL_TABS.filter((_, i) => visibilityRow[i])
    : PERSONAL_TABS;

  // Map a visible tab index back to the original PERSONAL_TABS index
  const toPersonalIdx = (visibleIdx) => {
    if (!visibilityRow) return visibleIdx;
    let count = -1;
    for (let i = 0; i < visibilityRow.length; i++) {
      if (visibilityRow[i]) count++;
      if (count === visibleIdx) return i;
    }
    return visibleIdx;
  };

  // ── Data state ───────────────────────────────────────────────────────────────
  const [profile, setProfile] = useState(null);
  const [extended, setExtended] = useState({});
  const [addresses, setAddresses] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [driveStatus, setDriveStatus] = useState({ connected: false, google_email: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ── Edit state ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);
  const [basicDraft, setBasicDraft] = useState({});
  const [contactDraft, setContactDraft] = useState({});
  const [basicDirty, setBasicDirty] = useState(false);
  const [contactDirty, setContactDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Photo upload ──────────────────────────────────────────────────────────────
  const fileRef = useRef(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  // ── Snackbar ──────────────────────────────────────────────────────────────────
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  // ── Load profile ─────────────────────────────────────────────────────────────
  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [profileData, addrData, credData, driveData] = await Promise.all([
        profileApi.getProfile(),
        profileApi.listAddresses(),
        profileApi.listCredentials(),
        profileApi.getDriveStatus().catch(() => ({ connected: false, google_email: null })),
      ]);
      setProfile(profileData);
      setExtended(profileData.extended || {});
      setAddresses(addrData.addresses || []);
      setCredentials(credData.credentials || []);
      setDriveStatus({
        connected:    Boolean(driveData.connected),
        drive_email:  driveData.drive_email  || null,
        signin_email: driveData.signin_email || null,
      });
      setBasicDraft({
        customer_name:  profileData.customer_name || '',
        first_name:     profileData.first_name || '',
        last_name:      profileData.last_name || '',
        email:          profileData.email || '',
        gender:         profileData.gender || '',
        dob:            profileData.dob || '',
        username:       profileData.username || '',
        is_public:      profileData.is_public,
      });
      setContactDraft({ ...(profileData.extended || {}) });
      setBasicDirty(false);
      setContactDirty(false);
    } catch {
      setError('Failed to load profile. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  // ── Basic draft change ────────────────────────────────────────────────────────
  const handleBasicChange = (field, value) => {
    setBasicDraft(d => ({ ...d, [field]: value }));
    setBasicDirty(true);
  };

  // ── Contact draft change ──────────────────────────────────────────────────────
  const handleContactChange = (field, value) => {
    setContactDraft(d => ({ ...d, [field]: value }));
    setContactDirty(true);
  };

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      if (activeTab === 0 && basicDirty) {
        const basicPayload = {
          customer_name: basicDraft.customer_name,
          gender:        basicDraft.gender,
          dob:           basicDraft.dob,
        };
        await profileApi.updateBasic(basicPayload);

        // Handle username + visibility separately
        if (basicDraft.is_public !== profile.is_public) {
          await profileApi.setVisibility(basicDraft.is_public);
        }
        if (basicDraft.username && basicDraft.username !== profile.username) {
          await profileApi.setUsername(basicDraft.username);
        }
        setBasicDirty(false);
        showSnack('Personal details saved');
      }
      if (activeTab === 1 && contactDirty) {
        await profileApi.updateContact(contactDraft);
        setContactDirty(false);
        showSnack('Contact info saved');
      }
      await loadProfile();
    } catch (err) {
      showSnack(err.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Photo upload ─────────────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) {
      showSnack('File too large. Max 5 MB.', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setCropSrc(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCopyProfileLink = async () => {
    if (!profile?.username) return;
    const link = `${window.location.origin}/profile/${profile.username}`;
    try {
      await navigator.clipboard.writeText(link);
      showSnack('Profile link copied');
    } catch {
      showSnack('Failed to copy link', 'error');
    }
  };

  const handleCropComplete = async (croppedBlob) => {
    setCropSrc(null);
    if (!croppedBlob) return;
    setPhotoUploading(true);
    try {
      const ext = 'jpg';
      const fileName = `photo_${Date.now()}.${ext}`;
      const { presigned_url, permanent_url } = await profileApi.getPhotoPresignedUrl(
        fileName, 'image/jpeg', profile.customer_id
      );
      await fetch(presigned_url, {
        method: 'PUT',
        body: croppedBlob,
        headers: { 'Content-Type': 'image/jpeg' },
      });
      const updatedPhoto = await profileApi.updatePhoto(permanent_url);
      setProfile(p => ({ ...p, profile_picture: updatedPhoto.profile_picture || permanent_url }));
      showSnack('Profile photo updated');
    } catch {
      showSnack('Photo upload failed', 'error');
    } finally {
      setPhotoUploading(false);
    }
  };

  // ── Address handlers ──────────────────────────────────────────────────────────
  const handleAddAddress = async (addr) => {
    try { await profileApi.addAddress(addr); await loadProfile(); showSnack('Address added'); }
    catch { showSnack('Failed to add address', 'error'); }
  };
  const handleUpdateAddress = async (id, addr) => {
    try { await profileApi.updateAddress(id, addr); await loadProfile(); showSnack('Address updated'); }
    catch { showSnack('Failed to update address', 'error'); }
  };
  const handleDeleteAddress = async (id) => {
    try { await profileApi.deleteAddress(id); await loadProfile(); showSnack('Address deleted'); }
    catch { showSnack('Failed to delete address', 'error'); }
  };
  const handleSetDefault = async (id) => {
    try { await profileApi.setDefaultAddress(id); await loadProfile(); showSnack('Default address set'); }
    catch { showSnack('Failed to set default', 'error'); }
  };

  const isReadOnly = isOrgContext && orgRole !== 'admin';
  const isDirty = !isReadOnly && ((activeTab === 0 && basicDirty) || (activeTab === 1 && contactDirty));

  // ── Render helpers ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Skeleton variant="circular" width={80} height={80} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="40%" height={32} />
              <Skeleton variant="text" width="25%" height={24} />
            </Box>
          </Stack>
        </Paper>
        <Skeleton variant="rounded" height={48} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={300} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 10 }}>
      <Container maxWidth="md" sx={{ pt: 3 }}>

        {/* ── Org context banner ───────────────────────────────────────────────── */}
        {isOrgContext && (
          <Alert
            severity="info"
            sx={{ mb: 2, borderRadius: 2 }}
            action={
              <Button size="small" color="inherit" onClick={() => { }}>
                {activeOrg?.org_name}
              </Button>
            }
          >
            Viewing organisation profile &mdash; <strong>{activeOrg?.org_name}</strong> &middot; Role: <strong>{orgRole}</strong>
          </Alert>
        )}

        {/* ── Header card ──────────────────────────────────────────────────────── */}
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            {/* Avatar with camera overlay */}
            <Box sx={{ position: 'relative', alignSelf: { xs: 'center', sm: 'auto' } }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <IconButton
                    size="small"
                    onClick={() => fileRef.current?.click()}
                    disabled={photoUploading}
                    sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, color: '#fff', width: 32, height: 32 }}
                  >
                    {photoUploading ? <CircularProgress size={16} color="inherit" /> : <CameraAltIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                }
              >
                <Avatar
                  src={profile?.profile_picture}
                  sx={{ width: 80, height: 80, fontSize: 32, bgcolor: 'primary.main', cursor: 'pointer' }}
                  onClick={() => fileRef.current?.click()}
                >
                  {(profile?.customer_name || '?')[0].toUpperCase()}
                </Avatar>
              </Badge>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFileSelect} />
            </Box>

            {/* Name + meta */}
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" fontWeight={700}>
                  {profile?.customer_name || 'Your Profile'}
                </Typography>
                <Tooltip title={profile?.is_public ? 'Public profile' : 'Private profile'}>
                  {profile?.is_public
                    ? <PublicIcon fontSize="small" color="success" />
                    : <LockIcon fontSize="small" color="action" />
                  }
                </Tooltip>
              </Stack>
              {profile?.username && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Link
                    component={RouterLink}
                    to={`/profile/${profile.username}`}
                    variant="body2"
                    color="text.secondary"
                    underline="hover"
                  >
                    @{profile.username}
                  </Link>
                  <Tooltip title="Copy profile link">
                    <IconButton size="small" onClick={handleCopyProfileLink}>
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  {profile?.is_public && (
                    <Link
                      component={RouterLink}
                      to={`/profile/${profile.username}`}
                      variant="caption"
                      underline="hover"
                      sx={{ ml: 1 }}
                    >
                      View your public profile page
                    </Link>
                  )}
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary">
                {profile?.phone ? `${profile?.country_code || '+44'} ${profile.phone}` : ''}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* ── Tabs ─────────────────────────────────────────────────────────────── */}
        <Paper variant="outlined" sx={{
          borderRadius: 3, overflow: 'hidden',
          '& .MuiInputBase-root.Mui-disabled': { backgroundColor: 'grey.100' },
          '& .MuiInputBase-root[readonly], & .MuiInputBase-root .MuiInputBase-input[readonly]': { backgroundColor: 'grey.100' },
        }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              px: 1,
              '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.3 },
            }}
          >
            {TABS.map(t => <Tab key={t} label={t} />)}
          </Tabs>

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {(() => {
              const personalIdx = toPersonalIdx(activeTab);
              const isReadOnly = isOrgContext && orgRole !== 'admin';

              return (
                <>
                  {/* Personal (tab 0) */}
                  {personalIdx === 0 && (
                    <BasicDetails
                      data={{ ...profile, ...basicDraft }}
                      onChange={handleBasicChange}
                      dirty={basicDirty}
                      saving={saving}
                      readOnly={isReadOnly}
                      isOrgContext={isOrgContext}
                      onPhoneAdded={(phone, countryCode) => {
                        setProfile(p => p ? { ...p, phone, country_code: countryCode || p.country_code || '+44', has_phone: true } : p);
                      }}
                      onEmailChanged={(email) => {
                        setProfile(p => p ? { ...p, email } : p);
                        setBasicDraft(d => ({ ...d, email }));
                      }}
                    />
                  )}

                  {/* Contact (tab 1) — Addresses + Contact Info sections */}
                  {personalIdx === 1 && (
                    <Stack spacing={4}>
                      <AddressManager
                        addresses={addresses}
                        onAdd={isReadOnly ? null : handleAddAddress}
                        onUpdate={isReadOnly ? null : handleUpdateAddress}
                        onDelete={isReadOnly ? null : handleDeleteAddress}
                        onSetDefault={isReadOnly ? null : handleSetDefault}
                        readOnly={isReadOnly}
                      />

                      <Divider />

                      <Box>
                        <Typography variant="subtitle1" fontWeight={600} mb={2}>Contact Details</Typography>
                        <ContactInfo
                          data={{ ...extended, ...contactDraft }}
                          onChange={handleContactChange}
                          dirty={contactDirty}
                          saving={saving}
                          readOnly={isReadOnly}
                        />
                      </Box>
                    </Stack>
                  )}

                  {/* Account (tab 2) */}
                  {personalIdx === 2 && (
                    <AccountSettings
                      customerId={profile?.customer_id}
                      credentials={credentials}
                      onCredentialsChange={async () => {
                        const credData = await profileApi.listCredentials();
                        setCredentials(credData.credentials || []);
                      }}
                      profileApi={profileApi}
                      phone={profile?.phone}
                      countryCode={profile?.country_code || '+44'}
                      driveConnected={driveStatus.connected}
                      driveEmail={driveStatus.drive_email}
                      googleEmail={driveStatus.signin_email}
                      hasAnthropicApiKey={profile?.has_anthropic_api_key}
                      anthropicApiKeyUpdatedAt={profile?.anthropic_api_key_updated_at}
                      hasGeminiApiKey={profile?.has_gemini_api_key}
                      geminiApiKeyUpdatedAt={profile?.gemini_api_key_updated_at}
                      plannerModel={profile?.planner_model}
                      plannerModelOptions={profile?.planner_model_options || []}
                      faceGroupingModel={profile?.face_grouping_model}
                      faceGroupingModelOptions={profile?.face_grouping_model_options || []}
                      captionModel={profile?.caption_model}
                      captionModelOptions={profile?.caption_model_options || []}
                      hasCaptionGeminiApiKey={profile?.has_caption_gemini_api_key}
                      captionGeminiApiKeyUpdatedAt={profile?.caption_gemini_api_key_updated_at}
                      onDriveStatusChange={(status) => setDriveStatus(s => ({ ...s, ...status }))}
                      onAnthropicKeyChange={(status) => setProfile(p => p ? { ...p, ...status } : p)}
                      onFaceGroupingModelChange={(status) => setProfile(p => p ? { ...p, ...status } : p)}
                    />
                  )}

                  {/* Privacy (tab 3) */}
                  {personalIdx === 3 && (
                    <PrivacySettings
                      profileApi={profileApi}
                      onAccountDeleted={() => showSnack('Account deleted')}
                    />
                  )}

                  {/* Business (tab 4) — org management panel when in org context, join/create otherwise */}
                  {personalIdx === 4 && (
                    isOrgContext
                      ? <OrgManagement
                          orgId={activeOrg?.org_id}
                          orgRole={orgRole}
                          onSnack={showSnack}
                        />
                      : <CorporateSection />
                  )}

                  {/* Integrations (tab 5) */}
                  {personalIdx === 5 && (
                    <IntegrationsSettings
                      profileApi={profileApi}
                      profile={profile}
                      onProfileChange={(updates) => setProfile(p => p ? { ...p, ...updates } : p)}
                    />
                  )}

                  {/* Notifications (tab 6) */}
                  {personalIdx === 6 && <NotificationsSettings />}

                  {/* Billing & Credits (tab 7) */}
                  {personalIdx === 7 && <BillingCredits profileApi={profileApi} />}
                </>
              );
            })()}
          </Box>
        </Paper>
      </Container>

      {/* ── Floating Save Bar - only when dirty ─────────────────────────────── */}
      <Fade in={isDirty}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: { xs: 64, sm: 24 },
            left: '50%',
            transform: 'translateX(-50%)',
            px: 3,
            py: 1.5,
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            zIndex: 1300,
            minWidth: 280,
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="body2" color="text.secondary">You have unsaved changes</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              onClick={() => { setBasicDirty(false); setContactDirty(false); loadProfile(); }}
              disabled={saving}
            >
              Discard
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              Save
            </Button>
          </Stack>
        </Paper>
      </Fade>

      {/* ── Image cropper ──────────────────────────────────────────────────────── */}
      {cropSrc && (
        <ImageCropper
          image={cropSrc}
          open={!!cropSrc}
          onCropComplete={handleCropComplete}
          onClose={() => setCropSrc(null)}
        />
      )}

      {/* ── Snackbar ───────────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile;
